import Spinner from "@/components/Spinner";
import { useDiabetesPatientsQuery, useOrganizationQuery } from "@/data/queries";
import { hasFeatureFlag } from "@/model/Organization";
import { DiabetesPatient } from "@/model/Patient";
import { formatDate, formatGestationalAge, isSameYearMonth } from "@/util/date-utils";
import { useStickyValue } from "@/util/hooks/useStickyValue";
import { LocalDate, ZoneId } from "@js-joda/core";
import orderBy from "lodash/orderBy";
import { useMemo, useState } from "react";
import HeaderCell from "./HeaderCell";
import { LiveInteractionDialog } from "./LiveInteractionDialog";
import MedicationListCell from "./MedicationListCell";
import { ClinicalStatusCell, ClinicalTimeCell, NoteButton } from "./PatientActionCells";
import PatientDataCell from "./PatientDataCell";
import { PatientEducationDialog } from "./PatientEducationDialog";
import PatientStatusCapsule from "./PatientStatusCapsule";
import PriorityIconWithTooltip, { PriorityIconWithTooltipProps } from "./PriorityIconWithTooltip";
import { stickyDataCell, stickyHeaderCell } from "./stickyColumns";
import { StickyNoteDialog } from "./StickyNoteDialog";
import { daysSinceReviewFor, formatDays, patientStatusOrder } from "./Utils";
import ViewLink from "./ViewLink";

const priorityIconOptions = (patient: DiabetesPatient): PriorityIconWithTooltipProps | null => {
  const { emergencyLow, criticalLow, criticalHigh } = patient.unreviewedReadings;
  if (emergencyLow > 0) {
    return {
      arrow: "double",
      arrowDirection: "down",
      color: "text-measurement-text-emergency",
      tooltipText: "Emergency low blood glucose",
    };
  } else if (criticalLow > 0) {
    return {
      arrow: "single",
      arrowDirection: "down",
      color: "text-measurement-text-warning",
      tooltipText: "Critical low blood glucose",
    };
  } else if (criticalHigh > 0) {
    return {
      arrow: "single",
      arrowDirection: "up",
      color: "text-measurement-text-emergency",
      tooltipText: "Critical high blood glucose",
    };
  } else {
    return null;
  }
};

const matchesSearch = (patient: DiabetesPatient, searchQuery: string) => {
  const needle = searchQuery.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  return [patient.firstName, patient.lastName, patient.medicalRecordNumber]
    .join(" ")
    .toLowerCase()
    .includes(needle);
};

type ActiveDialog = { type: "live" | "education" | "note"; patient: DiabetesPatient };

type DiabetesColumns = {
  medication: boolean;
  patientEducation: boolean;
  liveInteraction: boolean;
  clinicalTime: boolean;
  stickyNote: boolean;
};

const diabetesGridTemplateColumns = (columns: DiabetesColumns) =>
  [
    "1fr", // Last name
    "1fr", // First name
    "1fr", // MRN
    "1fr", // GA
    "minmax(max-content, 1fr)", // Protocol
    columns.medication && "auto",
    columns.patientEducation && "auto",
    columns.liveInteraction && "auto",
    columns.clinicalTime && "auto",
    "1fr", // Journey status
    "1fr", // Days since review
    "3rem", // Priority icon
    columns.stickyNote && "3.5rem", // Note
    "6rem", // Action
  ]
    .filter(Boolean)
    .join(" ");

const priorityStickyRight = (columns: { stickyNote: boolean }) =>
  columns.stickyNote ? "right-38" : "right-24";

const DiabetesPopulationList = ({ searchQuery = "" }: { searchQuery?: string }) => {
  const { isFetching, isError, data: patients = [] } = useDiabetesPatientsQuery();
  const { data: organization } = useOrganizationQuery();

  const columns: DiabetesColumns = {
    medication: hasFeatureFlag(organization, "MEDICATION_MANAGEMENT_DIABETES"),
    patientEducation: hasFeatureFlag(organization, "PATIENT_EDUCATION_DIABETES"),
    liveInteraction: hasFeatureFlag(organization, "TRACK_MONTHLY_LIVE_INTERACTION"),
    clinicalTime: hasFeatureFlag(organization, "TRACK_MONTHLY_CLINICAL_TIME"),
    stickyNote: hasFeatureFlag(organization, "CLINICIAN_APP_PATIENT_STICKY_NOTE"),
  };
  console.log(columns);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const displayActiveDialog = useStickyValue(activeDialog);

  const monthLabel = formatDate("MMM yyyy", LocalDate.now());
  const gridTemplateColumns = diabetesGridTemplateColumns(columns);

  const sortedPatients = useMemo(
    () =>
      orderBy(
        patients.filter((patient) => matchesSearch(patient, searchQuery)),
        [
          (patient) => patientStatusOrder(patient.status),
          (patient) => patient.unreviewedReadings.emergencyLow > 0,
          (patient) => daysSinceReviewFor(patient) ?? Number.MIN_VALUE,
          (patient) => patient.lastName,
          (patient) => patient.firstName,
        ],
        ["asc", "desc", "desc", "asc", "asc"]
      ),
    [patients, searchQuery]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="grid" style={{ gridTemplateColumns }}>
          <HeaderCell className="pe-6 ps-4">Last name</HeaderCell>
          <HeaderCell className="pe-6">First name</HeaderCell>
          <HeaderCell className="pe-6">MRN</HeaderCell>
          <HeaderCell className="pe-6">GA</HeaderCell>
          <HeaderCell className="pe-4">Protocol</HeaderCell>
          {columns.medication && <HeaderCell className="pe-6">Medication</HeaderCell>}
          {columns.patientEducation && (
            <HeaderCell className="pe-4">
              <span title="Patient education">Patient ed.</span>
            </HeaderCell>
          )}
          {columns.liveInteraction && (
            <HeaderCell className="pe-4">
              <div className="flex flex-col">
                <span title="Live interaction">Live int.</span>
                <span className="text-xs font-normal text-primary/50">{monthLabel}</span>
              </div>
            </HeaderCell>
          )}
          {columns.clinicalTime && (
            <HeaderCell className="pe-4">
              <div className="flex flex-col">
                <span title="Clinical time">Clin. time</span>
                <span className="text-xs font-normal text-primary/50">{monthLabel}</span>
              </div>
            </HeaderCell>
          )}
          <HeaderCell className="pe-4">Journey status</HeaderCell>
          <HeaderCell className="pe-4">Days since review</HeaderCell>
          <HeaderCell
            className={stickyHeaderCell({
              rightClass: priorityStickyRight(columns),
              withGradient: true,
            })}
          />
          {columns.stickyNote && (
            <HeaderCell className={stickyHeaderCell({ rightClass: "right-24" })}>
              Note
            </HeaderCell>
          )}
          <HeaderCell className={stickyHeaderCell({ rightClass: "right-0" })}>Action</HeaderCell>
          {sortedPatients.map((patient, i) => (
            <DiabetesPatientRow
              key={`patient-${patient.id}`}
              patient={patient}
              rowIndex={i}
              columns={columns}
              onLogEducation={() => setActiveDialog({ type: "education", patient })}
              onLogLiveInteraction={() => setActiveDialog({ type: "live", patient })}
              onOpenNote={() => setActiveDialog({ type: "note", patient })}
            />
          ))}
        </div>
      </div>
      {isFetching && patients.length === 0 && (
        <div className="flex items-center justify-center gap-2 p-4">
          <Spinner />
          <span>Loading...</span>
        </div>
      )}
      {isError ? (
        <div className="p-4 text-center italic text-red-600">
          Failed to load patients. Please try again later.
        </div>
      ) : !isFetching && sortedPatients.length === 0 ? (
        <div className="p-4 text-center italic text-primary/60">
          {patients.length === 0
            ? "No active Bloomlife Connects diabetes orders. Come back soon!"
            : "No patients match your search."}
        </div>
      ) : null}

      {organization && displayActiveDialog && (
        <>
          <LiveInteractionDialog
            key={`live-interaction-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "live"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            organization={organization}
            protocolType="diabetes"
            patientId={displayActiveDialog.patient.id}
            prescribingCaregiver={displayActiveDialog.patient.prescribingCaregiver}
          />
          <PatientEducationDialog
            key={`patient-education-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "education"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            organization={organization}
            prescriptionId={displayActiveDialog.patient.prescriptionId}
          />
          <StickyNoteDialog
            key={`sticky-note-dialog-${displayActiveDialog.patient.id}`}
            open={activeDialog?.type === "note"}
            onOpenChange={(open) => {
              if (!open) {
                setActiveDialog(null);
              }
            }}
            note={displayActiveDialog.patient.stickyNote}
            prescriptionId={displayActiveDialog.patient.prescriptionId}
          />
        </>
      )}
    </>
  );
};

const DiabetesPatientRow = ({
  patient,
  rowIndex,
  columns,
  onLogEducation,
  onLogLiveInteraction,
  onOpenNote,
}: {
  patient: DiabetesPatient;
  rowIndex: number;
  columns: DiabetesColumns;
  onLogEducation: () => void;
  onLogLiveInteraction: () => void;
  onOpenNote: () => void;
}) => {
  const daysSinceReview = daysSinceReviewFor(patient);
  const iconOptions = priorityIconOptions(patient);
  const isMonitoring = ["monitoring", "postpartum-monitoring"].includes(patient.status);
  const lastLiveInteractionDate =
    patient.lastLiveInteractionTime &&
    LocalDate.ofInstant(patient.lastLiveInteractionTime, ZoneId.SYSTEM);
  const educationProvidedAtDate = patient.educationProvidedAt
    ? LocalDate.ofInstant(patient.educationProvidedAt, ZoneId.SYSTEM)
    : null;
  const currentDate = LocalDate.now();
  const ga = patient.deliveryDate
    ? "Delivered"
    : patient.estimatedDateOfDelivery
      ? formatGestationalAge(patient.estimatedDateOfDelivery, LocalDate.now())
      : "-";

  return (
    <>
      <PatientDataCell
        rowIndex={rowIndex}
        title={patient.lastName}
        className="overflow-hidden pe-6 ps-4"
      >
        <span className="truncate" title={patient.lastName}>
          {patient.lastName ?? "-"}
        </span>
      </PatientDataCell>
      <PatientDataCell
        rowIndex={rowIndex}
        title={patient.firstName}
        className="overflow-hidden pe-6"
      >
        <span className="truncate" title={patient.firstName}>
          {patient.firstName ?? "-"}
        </span>
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-6">
        {patient.medicalRecordNumber}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-6">
        {ga}
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="overflow-hidden pe-4">
        <span className="truncate" title={patient.personalizedClinicalProtocol.name}>
          {patient.personalizedClinicalProtocol.name ?? "-"}
        </span>
      </PatientDataCell>
      {columns.medication && (
        <MedicationListCell rowIndex={rowIndex} medications={patient.medications} />
      )}
      {columns.patientEducation && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalStatusCell
            date={educationProvidedAtDate ? formatDate("MMM d", educationProvidedAtDate) : null}
            onAdd={onLogEducation}
          />
        </PatientDataCell>
      )}
      {columns.liveInteraction && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalStatusCell
            date={
              lastLiveInteractionDate && isSameYearMonth(lastLiveInteractionDate, currentDate)
                ? formatDate("MMM d", lastLiveInteractionDate)
                : null
            }
            onAdd={onLogLiveInteraction}
          />
        </PatientDataCell>
      )}
      {columns.clinicalTime && (
        <PatientDataCell rowIndex={rowIndex} className="pe-4">
          <ClinicalTimeCell minutes={patient.clinicalTimeForCurrentMonth.toMinutes()} />
        </PatientDataCell>
      )}
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        <PatientStatusCapsule status={patient.status} />
      </PatientDataCell>
      <PatientDataCell rowIndex={rowIndex} className="pe-4">
        {isMonitoring && daysSinceReview ? formatDays(Math.round(daysSinceReview)) : "-"}
      </PatientDataCell>
      <PatientDataCell
        rowIndex={rowIndex}
        className={stickyDataCell({
          rightClass: priorityStickyRight(columns),
          rowIndex,
          withGradient: true,
        })}
      >
        {iconOptions && <PriorityIconWithTooltip {...iconOptions} />}
      </PatientDataCell>
      {columns.stickyNote && (
        <PatientDataCell
          rowIndex={rowIndex}
          className={stickyDataCell({ rightClass: "right-24", rowIndex })}
        >
          <NoteButton unread={false} onClick={onOpenNote} />
        </PatientDataCell>
      )}
      <PatientDataCell
        rowIndex={rowIndex}
        className={stickyDataCell({ rightClass: "right-0", rowIndex })}
      >
        <ViewLink to={`/patients/${patient.id}/diabetes`} />
      </PatientDataCell>
    </>
  );
};

export default DiabetesPopulationList;
